import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination, ITEMS_PER_PAGE } from '@/components/Pagination';

const CompletedProduction = () => {
  const [production, setProduction] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedProduction, setSelectedProduction] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const result = production.filter(
        (p) =>
          p.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          getSoNumber(p.so_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
          (p.location && p.location.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFiltered(result);
    } else {
      setFiltered(production);
    }
    setCurrentPage(1);
  }, [searchTerm, production]);

  const fetchData = async () => {
    try {
      const [prodRes, soRes, usersRes] = await Promise.all([
        axios.get(`${API}/production-tracking/completed`),
        axios.get(`${API}/sales-orders`),
        axios.get(`${API}/users/list`)
      ]);
      setProduction(prodRes.data);
      setFiltered(prodRes.data);
      setSalesOrders(soRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to fetch completed production');
    } finally {
      setLoading(false);
    }
  };

  const getSoNumber = (soId) => {
    const so = salesOrders.find(s => s.id === soId);
    return so ? so.so_number : soId;
  };

  const showHistory = (prod) => {
    setSelectedProduction(prod);
    setHistoryDialogOpen(true);
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="completed-production-page">
      <div className="mb-6">
        <h1 className="text-4xl font-semibold text-[hsl(145,60%,10%)] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Completed Production</h1>
        <p className="text-sm text-[hsl(145,20%,40%)] mt-2">View production history</p>
      </div>

      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[hsl(145,20%,40%)]" size={18} />
          <Input
            placeholder="Search by product, SO, or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-completed"
          />
        </div>
      </div>

      <div className="bg-white rounded-sm border border-stone-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="completed-table">
            <thead className="bg-stone-50 border-b border-stone-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Product</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">SO Number</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Assigned To</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Completed At</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((prod) => (
                <tr key={prod.id} className="hover:bg-stone-50/50 transition-colors" data-testid={`completed-row-${prod.id}`}>
                  <td className="px-6 py-2 text-sm text-[hsl(145,60%,10%)]">{prod.product_name}</td>
                  <td className="px-6 py-2 text-sm">
                    <button
                      onClick={() => showHistory(prod)}
                      className="text-[hsl(145,60%,20%)] hover:underline font-medium"
                      data-testid={`so-link-${prod.id}`}
                    >
                      {getSoNumber(prod.so_id)}
                    </button>
                  </td>
                  <td className="px-6 py-2 text-sm text-[hsl(145,60%,10%)]">{prod.quantity}</td>
                  <td className="px-6 py-2 text-sm text-[hsl(145,20%,40%)]">{prod.location || 'N/A'}</td>
                  <td className="px-6 py-2 text-sm text-[hsl(145,20%,40%)]">
                    {prod.assigned_to ? users.find(u => u.id === prod.assigned_to)?.name || 'N/A' : 'Unassigned'}
                  </td>
                  <td className="px-6 py-2 text-sm text-[hsl(145,20%,40%)]">
                    {prod.completed_at ? new Date(prod.completed_at).toLocaleString() : 'N/A'}
                  </td>
                  <td className="px-6 py-2 text-sm">
                    <Button variant="ghost" size="sm" onClick={() => showHistory(prod)}>
                      View Details
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="p-8 text-center text-[hsl(145,20%,40%)]" data-testid="no-completed">
              No completed production found
            </div>
          )}
        </div>
        <Pagination currentPage={currentPage} totalItems={filtered.length} onPageChange={setCurrentPage} />
      </div>

      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Production Details</DialogTitle>
          </DialogHeader>
          {selectedProduction && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-stone-50 rounded-sm">
                <div>
                  <p className="text-xs text-[hsl(145,20%,40%)] uppercase tracking-wider font-bold">Product</p>
                  <p className="text-lg font-semibold text-[hsl(145,60%,10%)]">{selectedProduction.product_name}</p>
                </div>
                <div>
                  <p className="text-xs text-[hsl(145,20%,40%)] uppercase tracking-wider font-bold">SO Number</p>
                  <p className="text-lg font-semibold text-[hsl(145,60%,10%)]">{getSoNumber(selectedProduction.so_id)}</p>
                </div>
                <div>
                  <p className="text-xs text-[hsl(145,20%,40%)] uppercase tracking-wider font-bold">Quantity</p>
                  <p className="text-lg font-medium text-[hsl(145,20%,40%)]">{selectedProduction.quantity}</p>
                </div>
                <div>
                  <p className="text-xs text-[hsl(145,20%,40%)] uppercase tracking-wider font-bold">Location</p>
                  <p className="text-lg font-medium text-[hsl(145,20%,40%)]">{selectedProduction.location || 'N/A'}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-stone-100">
                  <span className="text-sm text-[hsl(145,20%,40%)]">Started</span>
                  <span className="text-sm font-medium text-[hsl(145,60%,10%)]">
                    {new Date(selectedProduction.updated_at).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-stone-100">
                  <span className="text-sm text-[hsl(145,20%,40%)]">Completed</span>
                  <span className="text-sm font-medium text-[hsl(145,60%,10%)]">
                    {selectedProduction.completed_at ? new Date(selectedProduction.completed_at).toLocaleString() : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-stone-100">
                  <span className="text-sm text-[hsl(145,20%,40%)]">Assigned To</span>
                  <span className="text-sm font-medium text-[hsl(145,60%,10%)]">
                    {selectedProduction.assigned_to ? users.find(u => u.id === selectedProduction.assigned_to)?.name || 'N/A' : 'Unassigned'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-stone-100">
                  <span className="text-sm text-[hsl(145,20%,40%)]">Updated By</span>
                  <span className="text-sm font-medium text-[hsl(145,60%,10%)]">
                    {users.find(u => u.id === selectedProduction.updated_by)?.name || selectedProduction.updated_by}
                  </span>
                </div>
              </div>

              <div className="p-4 bg-green-50 rounded-sm">
                <h4 className="text-sm font-semibold text-green-800 mb-2">Production Stages</h4>
                {selectedProduction.stage_history && selectedProduction.stage_history.length > 0 ? (
                  <div className="space-y-2">
                    {selectedProduction.stage_history.map((stageItem, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border border-green-200">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full bg-green-600" />
                          <span className="text-sm font-medium text-[hsl(145,60%,10%)]">
                            {stageItem.stage.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-[hsl(145,20%,40%)]">
                            Completed by: {stageItem.completed_by_name || users.find(u => u.id === stageItem.completed_by)?.name || 'Unknown'}
                          </p>
                          {stageItem.location && (
                            <p className="text-xs text-[hsl(145,20%,40%)]">
                              Loc: {stageItem.location}
                            </p>
                          )}
                          <p className="text-xs text-[hsl(145,20%,40%)]">
                            {new Date(stageItem.completed_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {Object.entries(selectedProduction.stages || {}).map(([stage, completed]) => (
                      <div key={stage} className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full ${completed ? 'bg-green-600' : 'bg-gray-300'}`} />
                        <span className="text-sm text-[hsl(145,60%,10%)]">
                          {stage.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompletedProduction;
